import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, FileText, Loader2 } from 'lucide-react';
import { documentAPI, TripDocument } from '../../api/documents';
import toast from 'react-hot-toast';

interface DocumentUploadProps {
  entityType: 'trip' | 'expense' | 'payment';
  entityId: number | null; // null when creating new entity
  documentType: 'TRIP_DOCUMENT' | 'EXPENSE_INVOICE' | 'EXPENSE_RECEIPT' | 'PAYMENT_RECEIPT' | 'PAYMENT_PROOF';
  onUploadSuccess?: (document: TripDocument) => void;
  onUploadError?: (error: string) => void;
  onUploadComplete?: () => void; // Callback after upload completes (for triggering refresh)
  disabled?: boolean;
  className?: string;
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  entityType,
  entityId,
  documentType,
  onUploadSuccess,
  onUploadError,
  onUploadComplete,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const previousEntityIdRef = useRef<number | null>(entityId);

  // Auto-upload pending files when entityId becomes available
  useEffect(() => {
    const wasNull = previousEntityIdRef.current === null;
    const nowHasId = entityId !== null;
    
    if (wasNull && nowHasId && uploadedFiles.length > 0 && !uploading) {
      // Entity was just created, upload pending files
      const filesToUpload = [...uploadedFiles];
      setUploadedFiles([]); // Clear immediately
      
      // Upload files sequentially
      (async () => {
        for (const file of filesToUpload) {
          if (entityId) {
            await uploadFile(file);
          }
        }
      })();
    }
    
    previousEntityIdRef.current = entityId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (!entityId) {
      // If entity doesn't exist yet, store files to upload after creation
      setUploadedFiles(Array.from(files));
      toast(t('documents.uploadAfterSave'), { icon: 'ℹ️' });
      return;
    }

    // Upload files immediately if entity exists
    for (const file of Array.from(files)) {
      await uploadFile(file);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file: File) => {
    if (!entityId) {
      toast.error(t('documents.saveEntityFirst'));
      return;
    }

    setUploading(true);
    try {
      let document: TripDocument;

      switch (entityType) {
        case 'trip':
          document = await documentAPI.uploadToTrip(entityId, file, documentType);
          break;
        case 'expense':
          document = await documentAPI.uploadToExpense(entityId, file, documentType);
          break;
        case 'payment':
          document = await documentAPI.uploadToPayment(entityId, file, documentType);
          break;
        default:
          throw new Error('Invalid entity type');
      }

      toast.success(t('documents.uploadSuccess'));
      onUploadSuccess?.(document);
      onUploadComplete?.(); // Trigger refresh in DocumentList
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('documents.uploadError');
      toast.error(errorMessage);
      onUploadError?.(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePendingFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadPendingFiles = async () => {
    if (!entityId) {
      toast.error(t('documents.saveEntityFirst'));
      return;
    }

    const filesToUpload = [...uploadedFiles];
    setUploadedFiles([]); // Clear immediately to prevent double upload

    for (const file of filesToUpload) {
      await uploadFile(file);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {t('documents.uploadDocuments')}
        </label>
        {uploadedFiles.length > 0 && entityId && (
          <button
            type="button"
            onClick={handleUploadPendingFiles}
            disabled={uploading}
            className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
          >
            {t('documents.uploadAll')}
          </button>
        )}
      </div>

      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          disabled || uploading
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50 cursor-pointer'
        }`}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || uploading}
        />

        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-2" />
            <p className="text-sm text-gray-600">{t('documents.uploading')}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload className="w-8 h-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">
              {t('documents.clickToUpload')}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t('documents.supportedFormats')} (Max 10MB)
            </p>
          </div>
        )}
      </div>

      {/* Pending files (when entity doesn't exist yet) */}
      {uploadedFiles.length > 0 && !entityId && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">{t('documents.pendingUpload')}:</p>
          {uploadedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-2 bg-gray-50 rounded border"
            >
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 truncate" title={file.name}>
                  {file.name}
                </span>
                <span className="text-xs text-gray-500 flex-shrink-0">
                  ({formatFileSize(file.size)})
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleRemovePendingFile(index)}
                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <p className="text-xs text-blue-600">{t('documents.willUploadAfterSave')}</p>
        </div>
      )}
    </div>
  );
};
